import Vue from 'vue'
import VueRouter from 'vue-router'
import { updateSeo } from '@/utils/seo'
import { siteConfig } from '@/config/site'
import { auth, initAuth } from '@/store/auth'
import { extractReferralCode, trackFriendLinkVisit } from '@/utils/friendLinkTrack'
import { stripAppQuery } from '@/utils/interactiveRoute'

Vue.use(VueRouter)

const originalPush = VueRouter.prototype.push
const originalReplace = VueRouter.prototype.replace

VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }
  return originalPush.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') throw err
  })
}

VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject)
  }
  return originalReplace.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') throw err
  })
}

const routes = [
  {
    path: '/wifi/:code',
    name: 'WifiShare',
    component: () => import('@/views/WifiShare.vue'),
    meta: {
      title: 'WiFi 连接分享',
      seo: {
        description: '扫码连接 WiFi',
        path: '/wifi',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/gift/:code',
    name: 'GiftBookShare',
    component: () => import('@/views/GiftBookShare.vue'),
    meta: {
      title: '礼薄分享',
      bareLayout: true,
      seo: {
        description: '查看礼薄分享',
        path: '/gift',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/comment/:code',
    name: 'StudentCommentShare',
    component: () => import('@/views/StudentCommentShare.vue'),
    meta: {
      title: '学生评语',
      bareLayout: true,
      seo: {
        description: '查看学生评语',
        path: '/comment',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/order/:code',
    name: 'OrderMenuShare',
    component: () => import('@/views/OrderMenuShare.vue'),
    meta: {
      title: '点餐菜单',
      bareLayout: true,
      seo: {
        description: '电子菜单点餐',
        path: '/order',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/electric/meter/:code',
    name: 'ElectricMeterShare',
    component: () => import('@/views/ElectricRoomShare.vue'),
    meta: {
      title: '电表抄录',
      bareLayout: true,
      seo: {
        description: '电表抄表分享',
        path: '/electric/meter',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/electric/:code',
    name: 'ElectricRoomShare',
    component: () => import('@/views/ElectricRoomShare.vue'),
    meta: {
      title: '电费记录',
      bareLayout: true,
      seo: {
        description: '电房抄表分享',
        path: '/electric',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/task/:code',
    name: 'TaskBoardShare',
    component: () => import('@/views/TaskBoardShare.vue'),
    meta: {
      title: '任务提交',
      bareLayout: true,
      seo: {
        description: '任务板分享协作',
        path: '/task',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/live/:code',
    name: 'LiveQrShare',
    component: () => import('@/views/LiveQrShare.vue'),
    meta: {
      title: '活码',
      bareLayout: true,
      seo: {
        description: '查看活码内容',
        path: '/live',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/receipt/check',
    name: 'ReceiptCheck',
    component: () => import('@/views/ReceiptCheck.vue'),
    meta: {
      title: '收据防伪核验',
      bareLayout: true,
      seo: {
        description: '核验收据是否由站内系统开具',
        path: '/receipt/check',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/receipt/check/:code',
    name: 'ReceiptCheckCode',
    component: () => import('@/views/ReceiptCheck.vue'),
    meta: {
      title: '收据防伪核验',
      bareLayout: true,
      seo: {
        description: '核验收据是否由站内系统开具',
        path: '/receipt/check',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/receipt/:code',
    name: 'ReceiptShare',
    component: () => import('@/views/ReceiptShare.vue'),
    meta: {
      title: '收据',
      bareLayout: true,
      seo: {
        description: '查看收据',
        path: '/receipt',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/card/:code',
    name: 'BusinessCardShare',
    component: () => import('@/views/BusinessCardShare.vue'),
    meta: {
      title: '电子名片',
      bareLayout: true,
      seo: {
        description: '查看电子名片',
        path: '/card',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/quote/check',
    name: 'QuoteCheck',
    component: () => import('@/views/QuoteCheck.vue'),
    meta: {
      title: '报价单真伪核验',
      bareLayout: true,
      seo: {
        description: '核验报价单是否由站内系统开具',
        path: '/quote/check',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/quote/check/:code',
    name: 'QuoteCheckCode',
    component: () => import('@/views/QuoteCheck.vue'),
    meta: {
      title: '报价单真伪核验',
      bareLayout: true,
      seo: {
        description: '核验报价单是否由站内系统开具',
        path: '/quote/check',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/quote/:code',
    name: 'QuoteShare',
    component: () => import('@/views/QuoteShare.vue'),
    meta: {
      title: '报价单',
      bareLayout: true,
      seo: {
        description: '查看报价单',
        path: '/quote',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/contract/:code',
    name: 'ContractShare',
    component: () => import('@/views/ContractShare.vue'),
    meta: {
      title: '电子合同',
      bareLayout: true,
      seo: {
        description: '查看并签署电子合同',
        path: '/contract',
        robots: 'noindex,nofollow'
      }
    }
  },
  {
    path: '/go/:code',
    name: 'FriendLinkRedirect',
    redirect: to => ({ path: '/', query: { ref: to.params.code } })
  },
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: {
      title: '首页',
      seo: {
        description: siteConfig.description,
        keywords: siteConfig.keywords,
        path: '/',
        robots: 'index,follow'
      }
    }
  },
  {
    path: '/articles',
    name: 'Articles',
    component: () => import('@/views/Articles.vue'),
    meta: {
      title: '文章列表',
      seo: {
        description: '浏览全部技术文章、软件推荐、教程实战与资源分享，支持分类筛选与搜索。',
        keywords: '文章列表,技术文章,软件推荐,教程,资源分享',
        path: '/articles',
        robots: 'index,follow'
      }
    }
  },
  {
    path: '/article/:id',
    name: 'ArticleDetail',
    component: () => import('@/views/ArticleDetail.vue'),
    meta: {
      title: '文章详情',
      dynamicSeo: true
    }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('@/views/About.vue'),
    meta: {
      title: '关于',
      seo: {
        description: '关于博主与博客介绍，专注前端开发、软件推荐与技术教程分享。',
        keywords: '关于,博主,技术栈,前端开发',
        path: '/about',
        robots: 'index,follow'
      }
    }
  },
  {
    path: '/copyright',
    name: 'Copyright',
    component: () => import('@/views/Copyright.vue'),
    meta: {
      title: '版权声明',
      seo: {
        description: '青苹果网站版权声明与内容使用规范，包含原创保护、转载规则、免责声明及侵权投诉流程。',
        keywords: '版权声明,转载规范,免责声明,知识产权',
        path: '/copyright',
        robots: 'index,follow'
      }
    }
  },
  {
    path: '/sponsor',
    name: 'Sponsor',
    component: () => import('@/views/Sponsor.vue'),
    meta: {
      title: '赞助会员',
      seo: {
        description: '赞助青苹果会员，解锁会员专享文章。提供月度、半年、年度、三年多种套餐。',
        keywords: '赞助会员,会员套餐,内容解锁',
        path: '/sponsor',
        robots: 'index,follow'
      }
    }
  },
  {
    path: '/tools',
    name: 'ToolsHub',
    component: () => import('@/views/tools/ToolsHub.vue'),
    meta: {
      title: '在线工具箱',
      seo: {
        description: 'PDF 工具箱、图片处理、二维码条码、站长工具、Apache/Nginx 配置转换等实用在线工具。',
        keywords: '在线工具,PDF工具,图片处理,二维码,站长工具',
        path: '/tools',
        robots: 'index,follow'
      }
    }
  },
  {
    path: '/tools/image-editor',
    name: 'ImageEditor',
    component: () => import('@/views/tools/ImageEditorFullscreen.vue'),
    meta: {
      title: '图片编辑器',
      bareLayout: true,
      seo: {
        description: '全屏图片编辑器：图层、文字、导出；顶栏保留站点 Logo 与登录。',
        keywords: '图片编辑器,在线设计,海报',
        path: '/tools/image-editor',
        robots: 'index,follow'
      }
    }
  },
  {
    path: '/tools/resume-editor',
    name: 'ResumeEditor',
    component: () => import('@/views/tools/ResumeEditorFullscreen.vue'),
    meta: {
      title: '在线简历编辑器',
      bareLayout: true,
      seo: {
        description: '全屏在线简历编辑器：模板、实时预览、导出 PDF；顶栏保留站点 Logo 与登录。',
        keywords: '简历编辑器,在线简历,PDF简历,简历模板',
        path: '/tools/resume-editor',
        robots: 'index,follow'
      }
    }
  },
  {
    path: '/tools/:id',
    name: 'ToolPage',
    component: () => import('@/views/tools/ToolPage.vue'),
    meta: {
      title: '在线工具',
      dynamicSeo: true
    }
  },
  {
    path: '/redirect',
    name: 'LinkRedirect',
    component: () => import('@/views/LinkRedirect.vue'),
    meta: {
      title: '外链跳转确认',
      seo: {
        description: '外链跳转安全确认页',
        robots: 'noindex,nofollow',
        path: '/redirect'
      }
    }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/Profile.vue'),
    meta: {
      title: '个人中心',
      seo: {
        description: '用户个人中心',
        robots: 'noindex,nofollow',
        path: '/profile'
      }
    }
  },
  {
    path: '/admin',
    component: () => import('@/views/admin/AdminLayout.vue'),
    meta: { requiresAdmin: true, title: '后台管理' },
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        component: () => import('@/views/admin/AdminDashboard.vue')
      },
      {
        path: 'articles',
        name: 'AdminArticles',
        component: () => import('@/views/admin/AdminArticles.vue')
      },
      {
        path: 'articles/new',
        name: 'AdminArticleCreate',
        component: () => import('@/views/admin/AdminArticleEdit.vue')
      },
      {
        path: 'articles/:id/edit',
        name: 'AdminArticleEdit',
        component: () => import('@/views/admin/AdminArticleEdit.vue')
      },
      {
        path: 'users',
        name: 'AdminUsers',
        component: () => import('@/views/admin/AdminUsers.vue')
      },
      {
        path: 'wifi-links',
        name: 'AdminWifiLinks',
        component: () => import('@/views/admin/AdminWifiLinks.vue')
      },
      {
        path: 'gift-books',
        name: 'AdminGiftBooks',
        component: () => import('@/views/admin/AdminGiftBooks.vue')
      },
      {
        path: 'student-comments',
        name: 'AdminStudentComments',
        component: () => import('@/views/admin/AdminStudentComments.vue')
      },
      {
        path: 'order-menus',
        name: 'AdminOrderMenus',
        component: () => import('@/views/admin/AdminOrderMenus.vue')
      },
      {
        path: 'electric-rooms',
        name: 'AdminElectricRooms',
        component: () => import('@/views/admin/AdminElectricRooms.vue')
      },
      {
        path: 'task-boards',
        name: 'AdminTaskBoards',
        component: () => import('@/views/admin/AdminTaskBoards.vue')
      },
      {
        path: 'vocal-separates',
        name: 'AdminVocalSeparates',
        component: () => import('@/views/admin/AdminVocalSeparates.vue')
      },
      {
        path: 'epub-translates',
        name: 'AdminEpubTranslates',
        component: () => import('@/views/admin/AdminEpubTranslates.vue')
      },
      {
        path: 'ocr',
        name: 'AdminOcr',
        component: () => import('@/views/admin/AdminOcr.vue')
      },
      {
        path: 'tool-usage',
        name: 'AdminToolUsage',
        component: () => import('@/views/admin/AdminToolUsage.vue')
      },
      {
        path: 'live-qrs',
        name: 'AdminLiveQrs',
        component: () => import('@/views/admin/AdminLiveQrs.vue')
      },
      {
        path: 'receipts',
        name: 'AdminReceipts',
        component: () => import('@/views/admin/AdminReceipts.vue')
      },
      {
        path: 'quotes',
        name: 'AdminQuotes',
        component: () => import('@/views/admin/AdminQuotes.vue')
      },
      {
        path: 'contracts',
        name: 'AdminContracts',
        component: () => import('@/views/admin/AdminContracts.vue')
      },
      {
        path: 'business-cards',
        name: 'AdminBusinessCards',
        component: () => import('@/views/admin/AdminBusinessCards.vue')
      },
      {
        path: 'orders',
        name: 'AdminOrders',
        component: () => import('@/views/admin/AdminOrders.vue')
      },
      {
        path: 'member-plans',
        name: 'AdminMemberPlans',
        component: () => import('@/views/admin/AdminMemberPlans.vue')
      },
      {
        path: 'comments',
        name: 'AdminComments',
        component: () => import('@/views/admin/AdminComments.vue')
      },
      {
        path: 'site-monitor',
        name: 'AdminSiteMonitor',
        component: () => import('@/views/admin/AdminSiteMonitor.vue'),
        meta: { requiresSuperAdmin: true }
      },
      {
        path: 'site-settings',
        name: 'AdminSiteSettings',
        component: () => import('@/views/admin/AdminSiteSettings.vue'),
        meta: { requiresSuperAdmin: true }
      },
      {
        path: 'static-settings',
        name: 'AdminStaticSettings',
        component: () => import('@/views/admin/AdminStaticSettings.vue'),
        meta: { requiresSuperAdmin: true }
      },
      {
        path: 'friend-links',
        name: 'AdminFriendLinks',
        component: () => import('@/views/admin/AdminFriendLinks.vue'),
        meta: { requiresSuperAdmin: true }
      },
      {
        path: 'product-recommends',
        name: 'AdminProductRecommends',
        component: () => import('@/views/admin/AdminProductRecommends.vue'),
        meta: { requiresSuperAdmin: true }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 }
  }
})

router.beforeEach(async (to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAdmin)) {
    if (!auth.ready) {
      await initAuth()
    }
    if (!auth.canAccessAdmin) {
      next({ path: '/profile' })
      return
    }
    if (to.matched.some(record => record.meta.requiresSuperAdmin) && !auth.isSuperAdmin) {
      next({ path: '/admin' })
      return
    }
  }
  next()
})

router.afterEach((to) => {
  // 网站监控：仅前台页面上报
  if (!to.path.startsWith('/admin')) {
    import('@/utils/siteMonitor')
      .then(m => m.trackSiteMonitorPageview(to.fullPath.split('?')[0] || to.path || '/'))
      .catch(() => {})
  }

  const refCode = extractReferralCode(to)
  if (refCode) {
    trackFriendLinkVisit(refCode, { landingPath: to.fullPath.split('?')[0] || '/' })
    if (to.query.ref) {
      const query = { ...to.query }
      delete query.ref
      router.replace({ path: to.path, query, hash: to.hash }).catch(() => {})
    }
  }

  const withoutApp = stripAppQuery(to)
  if (withoutApp) {
    router.replace(withoutApp).catch(() => {})
  }

  if (to.meta.dynamicSeo) return

  const seo = to.meta.seo || {}
  updateSeo({
    title: to.meta.title,
    description: seo.description,
    keywords: seo.keywords,
    path: to.fullPath.split('?')[0] || seo.path || '/',
    robots: seo.robots || 'index,follow'
  })
})

export default router
